import dayjs, { Dayjs } from "dayjs";

import React, { useCallback, useEffect, useState } from "react";

import { Checkbox, FormControlLabel, FormHelperText, RadioGroup, Stack } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import { TemplateSetAggregate } from "../../services/api";
import { OperationSearchParams } from "../../services/api/JobOperation";
import { BackfillStatus } from "../../services/utils/status";
import InputGroup from "../shared/InputGroup";

type SearchFilterComponentProps = {
  setParams: (params: OperationSearchParams | null) => void;
  availableSets: TemplateSetAggregate[];
};

const ParamsFilter: React.FC<SearchFilterComponentProps> = ({ setParams, availableSets }) => {
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [selectedBackfillStatuses, setSelectedBackfillStatuses] = useState<string[]>([]);
  const [selectedSetIDs, setSelectedSetIds] = useState<string[]>([]);

  const startTimeReference = dayjs().set("hours", 0).set("minutes", 0).set("seconds", 0);
  const endTimeReference = dayjs().set("hours", 23).set("minutes", 59).set("seconds", 0);

  const startDateError = useCallback(() => {
    if (startDate === null) {
      return "A start date must be selected";
    } else if (startDate.valueOf() > new Date().valueOf()) {
      return "Start date cannot be in the future";
    } else if (endDate && endDate?.valueOf() <= startDate.valueOf()) {
      return "The end date cannot be on or before the end date";
    }
    return "";
  }, [startDate, endDate]);

  const endDateError = useCallback(() => {
    if (endDate === null) {
      return "An end date must be selected";
    } else if (startDate && startDate?.valueOf() >= endDate.valueOf()) {
      return "The start date cannot be on or after the end date";
    }
    return "";
  }, [startDate, endDate]);

  useEffect(() => {
    // all fields mandatory except set IDs
    if (startDateError() === "" && endDateError() === "" && selectedBackfillStatuses.length > 0) {
      const start = dayjs(startDate);
      const end = dayjs(endDate);

      setParams({
        start: start.utc().format("YYYY-MM-DD"),
        starttime: start.utc().format("HH:mm:ss"),
        end: end.utc().format("YYYY-MM-DD"),
        endtime: end.utc().format("HH:mm:ss"),
        statuses: selectedBackfillStatuses,
        sets: selectedSetIDs,
      });
    } else {
      setParams(null);
    }
  }, [startDate, endDate, setParams, selectedBackfillStatuses, startDateError, endDateError, selectedSetIDs]);

  const handleDateChange = (date: Dayjs | null, type: "start" | "end") => {
    if (type === "start") {
      setStartDate(date);
    } else {
      setEndDate(date);
    }
  };

  const handleBackfillStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSelectedBackfillStatuses((prevStatuses) => {
      const newStatuses = new Set(prevStatuses);
      if (event.target.checked) {
        newStatuses.add(value);
      } else {
        newStatuses.delete(value);
      }

      return Array.from(newStatuses);
    });
  };

  const handleSetChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSelectedSetIds((prevSets) => {
      const newSets = new Set(prevSets);
      if (event.target.checked) {
        newSets.add(value);
      } else {
        newSets.delete(value);
      }

      return Array.from(newSets);
    });
  };

  const formatForLabel = (status: string): string => {
    status = status.toLowerCase();
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  return (
    <>
      <Stack direction={{ xs: "column", sm: "row" }} sx={{ mb: "20pt" }} spacing={{ xs: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateTimePicker
            label="Start Date"
            value={startDate}
            onChange={(date) => handleDateChange(date, "start")}
            sx={{ width: "100%" }}
            data-testid={"datepicker-start"}
            ampm={false}
            timeSteps={{ hours: 1, minutes: 1 }}
            referenceDate={startTimeReference}
            disableFuture
            format={"YYYY-MM-DD HH:mm"}
            slotProps={{
              textField: {
                fullWidth: true,
                variant: "outlined",
                error: startDateError() !== "",
                helperText: startDateError(),
              },
            }}
          />
          <DateTimePicker
            label="End Date"
            value={endDate}
            onChange={(date) => handleDateChange(date, "end")}
            sx={{ width: "100%" }}
            data-testid={"datepicker-end"}
            ampm={false}
            timeSteps={{ hours: 1, minutes: 1 }}
            referenceDate={endTimeReference}
            minDate={startDate}
            format={"YYYY-MM-DD HH:mm"}
            slotProps={{
              textField: {
                fullWidth: true,
                variant: "outlined",
                error: endDateError() !== "",
                helperText: endDateError(),
              },
            }}
          />
        </LocalizationProvider>
      </Stack>
      <InputGroup label="Statuses">
        <RadioGroup row={true}>
          {Object.keys(BackfillStatus).map((status) => (
            <FormControlLabel
              value={status}
              key={status}
              control={<Checkbox data-testid={`status-${status}`} onChange={handleBackfillStatusChange} />}
              label={formatForLabel(status)}
            />
          ))}
        </RadioGroup>
        {selectedBackfillStatuses.length === 0 && (
          <FormHelperText error>Please select at least 1 status.</FormHelperText>
        )}
      </InputGroup>
      <InputGroup label="Set IDs">
        <RadioGroup row={true}>
          {availableSets.map((set) => (
            <FormControlLabel
              value={set.id}
              key={set.id}
              control={<Checkbox data-testid={`set-${set.id}`} onChange={handleSetChange} />}
              label={`${set.id}-${set.name}`}
            />
          ))}
        </RadioGroup>
      </InputGroup>
    </>
  );
};

export default ParamsFilter;
